import React, { useEffect } from "react";
import ReportStepFrame from "components/report/ReportStepFrame";
import styles from "./ScoreRevealStep.module.scss";

/**
 * The screen carries no controls, so the report moves on by itself. The
 * number finishes forming at 1700ms and then stands complete and still for
 * about a second, which is the beat the learner reads it in.
 */
const REVEAL_TIMELINE_MS = 2800;

/**
 * Motion is dropped under `prefers-reduced-motion`, so the finished number is
 * on screen from the first frame and the same wait would read as a stalled
 * screen rather than a reveal.
 */
const REDUCED_MOTION_TIMELINE_MS = 1200;

const prefersReducedMotion = (): boolean =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

export interface ScoreRevealStepProps {
  /** The learner's overall index score, as the Index tab shows it. */
  score: number | string;
  /** What the score is out of. The designs show 100 throughout. */
  outOf?: number | string;
  /** The line that leads into the number, e.g. "You scored". */
  eyebrow: string;
  /** Moves the report on once the reveal has played. */
  onDone?: () => void;
}

/**
 * The report's second step: the learner's overall score, arriving.
 *
 * The number is the whole screen, so the frame's small corner readout is
 * left off here — repeating the score would say it twice, and the design
 * gives this screen no controls either: it is a beat, not a page. The reveal
 * itself is CSS: three staggered fade-ups across the design's 2s timeline,
 * dropped entirely under `prefers-reduced-motion`.
 *
 * The parts of the score are marked so the next screen can carry them from
 * here to the top of its own, rather than the number fading out and back.
 */
const ScoreRevealStep: React.FC<ScoreRevealStepProps> = ({
  score,
  outOf = 100,
  eyebrow,
  onDone,
}) => {
  useEffect(() => {
    if (!onDone) return undefined;

    const timer = window.setTimeout(
      onDone,
      prefersReducedMotion() ? REDUCED_MOTION_TIMELINE_MS : REVEAL_TIMELINE_MS
    );
    return () => window.clearTimeout(timer);
  }, [onDone]);

  return (
    <ReportStepFrame centred>
      <div className={styles.step} data-testid="score-reveal-step">
        <h1 className={styles.heading}>
          <span className={styles.eyebrow} data-score-part="eyebrow">
            {eyebrow}
          </span>
          <span className={styles.reading}>
            <span
              className={styles.score}
              data-testid="score-reveal-value"
              data-score-part="score"
            >
              {score}
            </span>
            <span className={styles.separator} data-score-part="separator">
              /
            </span>
            <span className={styles["out-of"]} data-score-part="out-of">
              {outOf}
            </span>
          </span>
        </h1>
      </div>
    </ReportStepFrame>
  );
};

export default ScoreRevealStep;
